import { VerticalEntity } from 'store/verticals/types'
import { Period } from 'utils/types'
import { byYearValueIsExist } from 'containers/VerticalPage/common/utils'
import { getYearFromPeriod, numberIsBetween } from 'containers/VerticalPage/common/utils'

interface RevenueSum {
  [key: string]: number
}

interface YearData {
  exact: { [key: number]: string }
  extrapolated: { [key: number]: string }
  reportedByYear: { [key: number]: string }
  total: { [key: number]: string }
  documented: { [key: number]: string }
  totalWithoutVat?: { [key: number]: string }
  unReportedByYear?: { [key: number]: string }
  totalTaxesPaidByCDTFA?: { [key: number]: string }
  totalTaxesPaidByIvix?: { [key: number]: string }
  [key: string]: { [key: number]: string } | undefined
}

export const getVerticalRevenueBySourcesFromEntity = async (
  entityData: VerticalEntity | null,
  period: Period,
  convertCurrency?: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>,
  currentCurrency?: string,
) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)

  if (convertCurrency && currentCurrency) {
    try {
      const revenueSum: Record<string, number> = {}

      for (let year = startYear; year <= endYear; year++) {
        const yearSources = entityData.revenueSources.map(source => ({
          name: source.platform,
          value: source.revenueByYear?.estimated?.[year] || 0,
        }))

        for (const source of yearSources) {
          if (source.value > 0) {
            const convertedValue = await convertCurrency(source.value, 'USD', currentCurrency, year)
            if (convertedValue !== null) {
              revenueSum[source.name] = (revenueSum[source.name] || 0) + convertedValue
            }
          }
        }
      }

      const sourcesSum = Object.keys(revenueSum).map(key => ({
        name: key,
        value: revenueSum[key],
      }))

      return sourcesSum
    } catch (error) {
      console.error('Error converting currency for sources pie chart data:', error)
    }
  }

  const sources = entityData.revenueSources.map(source => ({
    name: source.platform,
    value: (() => {
      let totalSum = 0
      const totalData = source.revenueByYear?.estimated

      for (const key in totalData) {
        if (
          Object.prototype.hasOwnProperty.call(totalData, key) &&
          numberIsBetween(parseInt(key), startYear, endYear)
        ) {
          totalSum += totalData[key]
        }
      }

      return totalSum
    })(),
  }))

  let revenueSum: RevenueSum = {}

  sources.forEach(source => {
    revenueSum = {
      ...revenueSum,
      [source.name]: source.value + (revenueSum[source.name] || 0),
    }
  })

  const sourcesSum = Object.keys(revenueSum).map(key => ({
    name: key,
    value: revenueSum[key],
  }))

  return sourcesSum
}

export const getVerticalRevenueByTotalFromEntity = async (
  entityData: VerticalEntity | null,
  period: Period,
  convertCurrency?: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>,
  currentCurrency?: string,
) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)

  let estimatedSum = 0

  if (convertCurrency && currentCurrency) {
    try {
      for (let year = startYear; year <= endYear; year++) {
        let yearSum = 0

        entityData.revenueSources.forEach(source => {
          const estimatedData = source.revenueByYear?.estimated

          if (estimatedData?.[year.toString()]) {
            yearSum += estimatedData[year.toString()]
          }
        })

        if (yearSum > 0) {
          const convertedSum = await convertCurrency(yearSum, 'USD', currentCurrency, year)
          if (convertedSum !== null) {
            estimatedSum += convertedSum
          }
        }
      }
    } catch (error) {
      console.error('Error converting currency for pie chart data:', error)
    }
  } else {
    entityData.revenueSources.forEach(source => {
      const estimatedData = source.revenueByYear?.estimated

      for (const key in estimatedData) {
        if (
          Object.prototype.hasOwnProperty.call(estimatedData, key) &&
          numberIsBetween(parseFloat(key), startYear, endYear)
        ) {
          estimatedSum += estimatedData[key]
        }
      }
    })
  }

  return [{ name: 'Estimated', value: estimatedSum }]
}

export const getTotalRevenueFromEntity = async (
  entityData: VerticalEntity | null,
  period: Period,
  noDataAsNA: boolean = false,
  convertCurrency?: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>,
  currentCurrency?: string,
) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)
  const data = entityData.totals.byYear as unknown as YearData
  const years = new Set<string>()

  const withoutVatIsExist = byYearValueIsExist(data as { [key: string]: { [key: number]: string } }, 'totalWithoutVat')

  for (const category in data) {
    const categoryData = data[category]
    if (categoryData) {
      Object.keys(categoryData).forEach(year => years.add(year))
    }
  }

  const result = await Promise.all(
    Array.from(years)
      .sort()
      .map(async year => {
        const yearNumber = parseInt(year)
        const yearDataObject: {
          date: string
          exact: number
          extrapolated: number
          documented: number
          estimated: number
          total: number
          reported: number
          unreported: number
          withoutVat?: number
          [key: string]: string | number | undefined
        } = {
          date: year,
          exact: Number(data?.exact?.[Number(year)] || 0),
          extrapolated: Number(data?.extrapolated?.[Number(year)] || 0),
          documented: Number(data?.documented?.[Number(year)] || 0),
          estimated: Number(data?.estimated?.[Number(year)] || 0),
          total: Number(data?.total?.[Number(year)] || 0),
          reported: Number(data?.reportedByYear?.[Number(year)] || 0),
          unreported: Number(data?.unReportedByYear?.[Number(year)] || 0),
        }

        if (withoutVatIsExist) {
          yearDataObject.withoutVat = Number(data?.totalWithoutVat?.[Number(year)] || 0)
        }

        if (convertCurrency && currentCurrency) {
          const numericKeys = [
            'exact',
            'extrapolated',
            'documented',
            'estimated',
            'total',
            'reported',
            'unreported',
            'withoutVat',
          ]

          for (const key of numericKeys) {
            if (typeof yearDataObject[key] === 'number' && yearDataObject[key] !== null) {
              try {
                const convertedValue = await convertCurrency(
                  yearDataObject[key] as number,
                  'USD',
                  currentCurrency,
                  yearNumber,
                )
                if (convertedValue !== null) {
                  yearDataObject[key] = convertedValue
                }
              } catch (error) {
                console.error(`Error converting ${key} for year ${year}:`, error)
              }
            }
          }
        }

        return yearDataObject
      }),
  )

  return result.filter(item => numberIsBetween(parseFloat(item.date), startYear, endYear))
}

export const getLabelsForFinancialOverview = (
  totalRevenueGraphData: {
    [key: string]: string | number
  }[],
) => {
  const values = [
    { key: 'total', value: 'Total Revenue' },
    { key: 'reported', value: 'Reported' },
    { key: 'unreported', value: 'Unreported' },
  ]

  if (totalRevenueGraphData.some(item => typeof item['withoutVat'] === 'number')) {
    values.push({ key: 'withoutVat', value: 'Revenue w/o vat' })
  }

  return values
}
